'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'framer-motion';
import {
	AlertTriangle,
	ArrowLeft,
	Building2,
	Check,
	Copy,
	KeyRound,
	LogOut,
	Monitor,
	Pencil,
	Plus,
	RotateCw,
	Smartphone,
	Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { UAParser } from 'ua-parser-js';
import { AsyncConfirmButton } from '@/components/async-confirm-button/async-confirm-button';
import { infoToast } from '@/components/info-toast/info-toast';
import { LanguageSelector } from '@/components/language-selector/language-selector';
import RegisterTenantModal from '@/components/register-tenant/register-tenant.modal';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { FilePreview } from '@/components/ui/file-preview';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { usePersistedLanguage } from '@/hooks/use-sync-language';
import { clearTokens } from '@/lib/auth';
import { extractErrorText } from '@/lib/error';
import { clearFingerprint, getFingerprint } from '@/lib/fingerprint';
import { getTenantSessions } from '@/lib/tenant-session';
import {
	type Language,
	useCreateOrRegenerateApiKey,
	useDeleteApiKey,
	useDeleteMe,
	useDeleteMyDevice,
	useMe,
	useMyTenant,
	useUpdateMe,
} from '@/lib/user';
import {
	'common.cancel' as common_cancel,
	'common.confirm' as common_confirm,
	'user.profile.api_keys.actions.create' as profile_api_keys_action_create,
	'user.profile.api_keys.actions.delete' as profile_api_keys_action_delete,
	'user.profile.api_keys.actions.regenerate' as profile_api_keys_action_regenerate,
	'user.profile.api_keys.empty.description' as profile_api_keys_empty_description,
	'user.profile.api_keys.empty.title' as profile_api_keys_empty_title,
	'user.profile.api_keys.section.subtitle' as profile_api_keys_section_subtitle,
	'user.profile.api_keys.section.title' as profile_api_keys_section_title,
	'user.profile.api_keys.toasts.delete.error_description' as profile_api_keys_toast_delete_error_description,
	'user.profile.api_keys.toasts.delete.error_title' as profile_api_keys_toast_delete_error_title,
	'user.profile.api_keys.toasts.delete.loading_description' as profile_api_keys_toast_delete_loading_description,
	'user.profile.api_keys.toasts.delete.loading_title' as profile_api_keys_toast_delete_loading_title,
	'user.profile.api_keys.toasts.delete.success_description' as profile_api_keys_toast_delete_success_description,
	'user.profile.api_keys.toasts.delete.success_title' as profile_api_keys_toast_delete_success_title,
	'user.profile.api_keys.toasts.generate.error_description' as profile_api_keys_toast_generate_error_description,
	'user.profile.api_keys.toasts.generate.error_title' as profile_api_keys_toast_generate_error_title,
	'user.profile.api_keys.toasts.generate.loading_description' as profile_api_keys_toast_generate_loading_description,
	'user.profile.api_keys.toasts.generate.loading_title' as profile_api_keys_toast_generate_loading_title,
	'user.profile.api_keys.toasts.generate.success_description' as profile_api_keys_toast_generate_success_description,
	'user.profile.api_keys.toasts.generate.success_title' as profile_api_keys_toast_generate_success_title,
	'user.profile.header.back_to_chat' as profile_back_to_chat,
	'user.profile.edit.cancel' as profile_cancel,
	'user.profile.header.create_tenant' as profile_create_tenant,
	'user.profile.danger_zone.subtitle' as profile_danger_zone_subtitle,
	'user.profile.danger_zone.title' as profile_danger_zone_title,
	'user.profile.devices.revoke.error_description' as profile_device_revoke_error_description,
	'user.profile.devices.revoke.error_title' as profile_device_revoke_error_title,
	'user.profile.devices.revoke.loading_description' as profile_device_revoke_loading_description,
	'user.profile.devices.revoke.loading_title' as profile_device_revoke_loading_title,
	'user.profile.devices.revoke.success_description' as profile_device_revoke_success_description,
	'user.profile.devices.revoke.success_title' as profile_device_revoke_success_title,
	'user.profile.devices.added_on' as profile_devices_added_on,
	'user.profile.devices.empty' as profile_devices_empty,
	'user.profile.devices.subtitle' as profile_devices_subtitle,
	'user.profile.devices.title' as profile_devices_title,
	'user.profile.edit.display_name_label' as profile_display_name_label,
	'user.profile.header.subtitle' as profile_header_subtitle,
	'user.profile.header.title' as profile_header_title,
	'user.profile.edit.profile_image_label' as profile_profile_image_label,
	'user.profile.edit.save' as profile_save,
	'sidebar.tenant_switch' as sidebar_tenant_switch,
	'user.profile.api_keys.actions.not_enabled' as user_profile_api_keys_actions_not_enabled,
	'user.profile.api_keys.delete_modal.description' as user_profile_api_keys_delete_modal_description,
	'user.profile.api_keys.delete_modal.title' as user_profile_api_keys_delete_modal_title,
	'user.profile.api_keys.one_time_modal.client_id_label' as user_profile_api_keys_one_time_modal_client_id_label,
	'user.profile.api_keys.one_time_modal.copy_aria_label' as user_profile_api_keys_one_time_modal_copy_aria_label,
	'user.profile.api_keys.one_time_modal.copy_client_id_aria_label' as user_profile_api_keys_one_time_modal_copy_client_id_aria_label,
	'user.profile.api_keys.one_time_modal.description' as user_profile_api_keys_one_time_modal_description,
	'user.profile.api_keys.one_time_modal.saved_button' as user_profile_api_keys_one_time_modal_saved_button,
	'user.profile.api_keys.one_time_modal.security_note' as user_profile_api_keys_one_time_modal_security_note,
	'user.profile.api_keys.one_time_modal.title' as user_profile_api_keys_one_time_modal_title,
	'user.profile.delete_account.description' as user_profile_delete_account_description,
	'user.profile.delete_account.subtitle' as user_profile_delete_account_subtitle,
	'user.profile.delete_account.title' as user_profile_delete_account_title,
	'user.profile.devices.cant_delete' as user_profile_devices_cant_delete,
	'user.profile.devices.current' as user_profile_devices_current,
	'user.profile.devices.delete' as user_profile_devices_delete,
	'user.profile.edit.label' as user_profile_edit_label,
	'user.profile.header.logout' as user_profile_header_logout,
	'user.profile.info.messages_used' as user_profile_info_messages_used,
} from '@/paraglide/messages.js';
import { isLocale, setLocale } from '@/paraglide/runtime';

export const Route = createFileRoute('/_private/user')({
	component: ProfilePage,
	head: () => ({
		meta: [
			{ title: `Nexata | ${profile_header_title()}` },
			{ name: 'robots', content: 'noindex' },
		],
	}),
});

export default function ProfilePage() {
	const navigate = useNavigate();
	const { data: me, isLoading: meLoading } = useMe();
	const { data: tenant } = useMyTenant();
	const updateMe = useUpdateMe();
	const [editing, setEditing] = useState(false);
	const [displayName, setDisplayName] = useState('');
	const [language, setLanguage] = usePersistedLanguage();
	const [file, setFile] = useState<File | null>(null);
	const deleteMyDevice = useDeleteMyDevice();
	const deleteMe = useDeleteMe();
	const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
	const createOrRegenerateApiKey = useCreateOrRegenerateApiKey();
	const deleteApiKey = useDeleteApiKey();
	const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
	const [generatedApiKey, setGeneratedApiKey] = useState<string | null>(null);
	const [deleteApiKeyOpen, setDeleteApiKeyOpen] = useState(false);
	const [registerOpen, setRegisterOpen] = useState(false);
	const [actualFingerprint, setActualFingerprint] = useState<string | null>(
		null,
	);

	// Sync display name from server
	useEffect(() => {
		if (me?.displayName) setDisplayName(me.displayName);
	}, [me]);

	// Get Fingerprint
	useEffect(() => {
		async function loadFingerprint() {
			const fp = await getFingerprint();
			setActualFingerprint(fp);
		}
		loadFingerprint();
	}, []);

	// Cambio de Idioma
	const handleLanguageChange = (lang: Language) => {
		setLanguage(lang);
		if (isLocale(lang)) setLocale(lang, { reload: false });

		updateMe({ language: lang });
	};

	// Cambio de Tenant
	const [tenantCount, setTenantCount] = useState(0);

	useEffect(() => {
		const sessions = getTenantSessions();
		setTenantCount(Array.isArray(sessions) ? sessions.length : 0);
	}, []);

	const canSwitchTenant = tenantCount > 1;

	// Skeleton Loading
	if (meLoading) return <ProfileSkeleton />;
	if (!me) return null;

	// UpdateMe
	async function handleSave() {
		await updateMe({
			language,
			displayName: displayName !== me?.displayName ? displayName : undefined,
			pfp: file ?? undefined,
		});

		setEditing(false);
		setFile(null);
	}

	// Delete Device
	async function handleDeleteDevice(
		fingerprint: string,
		isLogout: boolean = false,
	) {
		const actualFingerprint = await getFingerprint();
		if (!isLogout && fingerprint === actualFingerprint) {
			await handleLogout();
			return;
		}

		const toastId = infoToast.loading({
			title: profile_device_revoke_loading_title(),
			description: profile_device_revoke_loading_description(),
		});

		try {
			await deleteMyDevice(fingerprint);
			infoToast.success(toastId, {
				title: profile_device_revoke_success_title(),
				description: profile_device_revoke_success_description(),
			});
		} catch (_error) {
			infoToast.error(toastId, {
				title: profile_device_revoke_error_title(),
				description:
					extractErrorText(_error) ?? profile_device_revoke_error_description(),
			});
		}
	}

	// CreateOrRegenerateApiKey
	async function handleCreateOrRegenerateApiKey() {
		const toastId = infoToast.loading({
			title: profile_api_keys_toast_generate_loading_title(),
			description: profile_api_keys_toast_generate_loading_description(),
		});

		try {
			const apiKey = await createOrRegenerateApiKey();
			setGeneratedApiKey(apiKey);
			setApiKeyModalOpen(true);

			infoToast.success(toastId, {
				title: profile_api_keys_toast_generate_success_title(),
				description: profile_api_keys_toast_generate_success_description(),
			});
		} catch (_error) {
			infoToast.error(toastId, {
				title: profile_api_keys_toast_generate_error_title(),
				description:
					extractErrorText(_error) ??
					profile_api_keys_toast_generate_error_description(),
			});
		}
	}

	// DeleteApiKey
	async function handleDeleteApiKey() {
		const toastId = infoToast.loading({
			title: profile_api_keys_toast_delete_loading_title(),
			description: profile_api_keys_toast_delete_loading_description(),
		});

		try {
			await deleteApiKey();

			infoToast.success(toastId, {
				title: profile_api_keys_toast_delete_success_title(),
				description: profile_api_keys_toast_delete_success_description(),
			});

			setDeleteApiKeyOpen(false);
		} catch (_error) {
			infoToast.error(toastId, {
				title: profile_api_keys_toast_delete_error_title(),
				description:
					extractErrorText(_error) ??
					profile_api_keys_toast_delete_error_description(),
			});
		}
	}

	// UserAgent Helper
	function formatUserAgent(ua: string | null | undefined) {
		if (!ua) return 'Unknown device';

		const parser = new UAParser(ua);
		const { browser, os, cpu, device } = parser.getResult();

		const parts = [
			browser.name && browser.version
				? `${browser.name} ${browser.version}`
				: browser.name,
			os.name && os.version ? `${os.name} ${os.version}` : os.name,
			device.type ?? 'Desktop',
			cpu.architecture,
		];

		return parts.filter(Boolean).join(' · ');
	}

	// Icon Device Helper
	function getDeviceIcon(ua: string | null | undefined) {
		if (!ua) return <Monitor className="h-4 w-4" />;

		const parser = new UAParser(ua);
		const { device } = parser.getResult();

		if (device.type === 'mobile' || device.type === 'tablet')
			return <Smartphone className="h-4 w-4" />;
		return <Monitor className="h-4 w-4" />;
	}

	// Handle LogOut
	async function handleLogout() {
		try {
			const fingerprint = await getFingerprint();
			await handleDeleteDevice(fingerprint, true);

			clearTokens();
			clearFingerprint();
			localStorage.clear();
			sessionStorage.clear();
			window.location.href = '/login';
		} catch (_e) {}
	}

	return (
		<div className="flex flex-col gap-6 px-3 py-4 w-full max-w-screen-2xl mx-auto">
			{/* Header */}
			<header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
						{profile_header_title()}
					</h1>
					<p className="text-sm text-muted-foreground">
						{profile_header_subtitle()}
					</p>
				</div>

				<div className="flex items-center gap-2 flex-wrap">
					<Button
						className="cursor-pointer"
						variant="outline"
						onClick={() => navigate({ to: '/chat', search: { id: undefined } })}
					>
						<ArrowLeft className="h-4 w-4 mr-2" />
						{profile_back_to_chat()}
					</Button>

					{canSwitchTenant && (
						<Button
							variant="outline"
							className="cursor-pointer"
							onClick={() => {
								const sessions = getTenantSessions();
								if (!sessions?.length) return;
								const encoded = encodeURIComponent(
									btoa(JSON.stringify(sessions)),
								);
								navigate({
									to: '/tenant-selection',
									search: { sessions: encoded },
								});
							}}
						>
							<Building2 className="h-4 w-4 mr-2" />
							{sidebar_tenant_switch()}
						</Button>
					)}

					{!canSwitchTenant && (
						<Button
							variant="outline"
							className="cursor-pointer"
							onClick={() => setRegisterOpen(true)}
						>
							<Plus className="h-4 w-4 mr-2" />
							{profile_create_tenant()}
						</Button>
					)}

					<Button
						className="cursor-pointer"
						variant="destructive"
						onClick={handleLogout}
					>
						<LogOut className="h-4 w-4 mr-2" />
						{user_profile_header_logout()}
					</Button>
				</div>
			</header>

			<Separator />

			<Card className="bg-sidebar" data-tour="user-profile-card">
				<CardContent className="p-6 space-y-6">
					<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
						<div className="flex items-center gap-5">
							<Avatar className="h-16 w-16">
								<AvatarImage src={me.pfpUrl ?? undefined} />
								<AvatarFallback>
									{(me.displayName ?? me.email)[0]?.toUpperCase()}
								</AvatarFallback>
							</Avatar>

							<div className="space-y-1">
								<div className="flex items-center gap-2">
									<h2 className="text-lg font-semibold">
										{me.displayName ?? 'User'}
									</h2>
									{me.isAdmin && <Badge variant="destructive">Admin</Badge>}

									<Tooltip>
										<TooltipTrigger>
											<Button
												className="cursor-pointer"
												size="icon"
												variant="ghost"
												onClick={() => setEditing((v) => !v)}
											>
												<Pencil className="h-4 w-4" />
											</Button>
										</TooltipTrigger>
										<TooltipContent>{user_profile_edit_label()}</TooltipContent>
									</Tooltip>
								</div>

								<p className="text-sm text-muted-foreground">{me.email}</p>
							</div>
						</div>

						<div className="flex items-center gap-1">
							<div className="flex items-center gap-4 rounded-lg border border-border bg-muted/40 px-4 py-2">
								<LanguageSelector
									value={language}
									onChange={handleLanguageChange}
								/>
								<Separator orientation="vertical" className="h-6" />
								<span className="text-sm text-muted-foreground">
									{user_profile_info_messages_used()}
								</span>
								<Separator orientation="vertical" className="h-6" />
								<span className="text-xl font-semibold leading-none text-primary">
									{me.messageCount}
								</span>
							</div>
						</div>
					</div>

					<AnimatePresence>
						{editing && (
							<motion.div
								initial={{ opacity: 0, height: 0 }}
								animate={{ opacity: 1, height: 'auto' }}
								exit={{ opacity: 0, height: 0 }}
								transition={{ duration: 0.25 }}
								className="overflow-hidden"
							>
								<Separator className="my-6" />

								<div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 py-2 px-2">
									<div className="flex flex-col md:flex-row md:items-start gap-6 flex-1">
										<div className="flex flex-col gap-2">
											<label className="text-sm font-medium">
												{profile_display_name_label()}
											</label>
											<Input
												className="w-full md:w-64 h-10"
												value={displayName}
												onChange={(e) => setDisplayName(e.target.value)}
											/>
										</div>

										<div className="flex flex-col gap-2">
											<label className="text-sm font-medium">
												{profile_profile_image_label()}
											</label>

											<div className="flex items-center gap-3 min-h-10">
												<Input
													type="file"
													accept="image/*"
													className="w-full sm:w-56 h-10 overflow-hidden file:hidden"
													onChange={(e) => setFile(e.target.files?.[0] ?? null)}
												/>

												<AnimatePresence initial={false}>
													{file && (
														<motion.div
															initial={{ opacity: 0, scale: 0.96 }}
															animate={{ opacity: 1, scale: 1 }}
															exit={{ opacity: 0, scale: 0.96 }}
															transition={{ duration: 0.15 }}
															className="flex items-center h-10"
														>
															<FilePreview
																file={file}
																onRemove={() => setFile(null)}
															/>
														</motion.div>
													)}
												</AnimatePresence>
											</div>
										</div>
									</div>

									<div className="flex items-center justify-end gap-2 pt-7 shrink-0">
										<Button
											className="cursor-pointer"
											variant="ghost"
											onClick={() => {
												setEditing(false);
												setFile(null);
												setDisplayName(me.displayName ?? '');
												setLanguage(me.language);
											}}
										>
											{profile_cancel()}
										</Button>

										<AsyncConfirmButton onClick={handleSave}>
											{profile_save()}
										</AsyncConfirmButton>
									</div>
								</div>
							</motion.div>
						)}
					</AnimatePresence>

					<Separator />

					<div className="space-y-4" data-tour="user-devices">
						<div>
							<h2 className="text-lg font-semibold">
								{profile_devices_title()}
							</h2>
							<p className="text-sm text-muted-foreground">
								{profile_devices_subtitle()}
							</p>
						</div>

						{me.devices.length === 0 && (
							<p className="text-sm text-muted-foreground">
								{profile_devices_empty()}
							</p>
						)}

						<div className="space-y-3">
							{[...me.devices]
								.sort((a, b) => {
									const aFirst = a.fingerprint === actualFingerprint ? 0 : 1;
									const bFirst = b.fingerprint === actualFingerprint ? 0 : 1;
									return aFirst - bFirst;
								})
								.map((device) => {
									const isCurrent = device.fingerprint === actualFingerprint;

									return (
										<div
											key={device.id}
											className="
  										flex items-center justify-between
  										rounded-lg border border-border
  										px-4 py-3
  										hover:bg-muted/40
  										transition-colors
  									"
										>
											<div className="space-y-1 min-w-0">
												<div className="flex items-center gap-2">
													<span className="text-muted-foreground">
														{getDeviceIcon(device.userAgent)}
													</span>
													<p className="text-sm font-medium truncate">
														{formatUserAgent(device.userAgent)}
													</p>
													{isCurrent && (
														<Badge variant="destructive" className="text-xs">
															{user_profile_devices_current()}
														</Badge>
													)}
												</div>

												<p className="text-xs text-muted-foreground truncate">
													{device.userAgent}
												</p>

												<p className="text-xs text-muted-foreground">
													{profile_devices_added_on()}{' '}
													{new Date(device.createdAt).toLocaleDateString()}
												</p>
											</div>

											<Tooltip>
												<TooltipTrigger>
													<span>
														<Button
															variant="destructive"
															size="icon"
															className={`${isCurrent ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
															disabled={isCurrent}
															onClick={() =>
																!isCurrent &&
																handleDeleteDevice(device.fingerprint)
															}
														>
															<Trash2 className="w-5 h-5" />
														</Button>
													</span>
												</TooltipTrigger>

												<TooltipContent>
													{isCurrent
														? user_profile_devices_cant_delete()
														: user_profile_devices_delete()}
												</TooltipContent>
											</Tooltip>
										</div>
									);
								})}
						</div>
					</div>
				</CardContent>
			</Card>

			<Card className="bg-sidebar" data-tour="user-api-keys">
				<CardContent className="p-6 space-y-6">
					<div className="space-y-3">
						<div>
							<h2 className="text-lg font-semibold">
								{profile_api_keys_section_title()}
							</h2>
							<p className="text-sm text-muted-foreground">
								{profile_api_keys_section_subtitle()}
							</p>
						</div>
						{me.hasApiKey ? (
							<div className="flex flex-col sm:flex-row sm:items-center gap-3">
								<div className="flex-1">
									<div
										className="
									flex items-center
									h-10 px-3
									rounded-md border border-border
									bg-muted/40
									font-mono text-sm
									text-muted-foreground
									tracking-widest
									select-none
									"
									>
										*****************************
									</div>
								</div>

								<TooltipProvider>
									<div className="flex items-center gap-2 shrink-0">
										<Tooltip>
											<TooltipTrigger>
												<Button
													variant="outline"
													size="icon"
													className="cursor-pointer"
													onClick={handleCreateOrRegenerateApiKey}
													aria-label="Regenerate API key"
												>
													<RotateCw className="h-4 w-4" />
												</Button>
											</TooltipTrigger>
											<TooltipContent>
												<p>{profile_api_keys_action_regenerate()}</p>
											</TooltipContent>
										</Tooltip>

										{/* Delete */}
										<Tooltip>
											<TooltipTrigger>
												<Button
													variant="destructive"
													size="icon"
													className="cursor-pointer"
													onClick={() => setDeleteApiKeyOpen(true)}
													aria-label="Delete API key"
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</TooltipTrigger>
											<TooltipContent>
												<p>{profile_api_keys_action_delete()}</p>
											</TooltipContent>
										</Tooltip>
									</div>
								</TooltipProvider>
							</div>
						) : (
							<div className="flex items-center justify-between gap-4 rounded-lg border border-dashed border-border bg-muted/30 px-4 py-4">
								<div>
									<p className="text-sm font-medium">
										{profile_api_keys_empty_title()}
									</p>
									<p className="text-xs text-muted-foreground">
										{profile_api_keys_empty_description()}
									</p>
								</div>

								<Tooltip>
									<TooltipTrigger>
										<span>
											<Button
												size="sm"
												className={
													!tenant?.hasApiAccess
														? 'cursor-not-allowed'
														: 'cursor-pointer'
												}
												disabled={!tenant?.hasApiAccess}
												onClick={handleCreateOrRegenerateApiKey}
											>
												{profile_api_keys_action_create()}
											</Button>
										</span>
									</TooltipTrigger>

									{!tenant?.hasApiAccess && (
										<TooltipContent>
											{user_profile_api_keys_actions_not_enabled()}
										</TooltipContent>
									)}
								</Tooltip>
							</div>
						)}
					</div>

					<Separator />

					<div
						className="
					rounded-lg border border-destructive/40
					bg-destructive/5
					p-5
					space-y-3
					"
					>
						<div>
							<h3 className="text-sm font-semibold text-destructive">
								{profile_danger_zone_title()}
							</h3>
							<p className="text-xs text-muted-foreground">
								{profile_danger_zone_subtitle()}
							</p>
						</div>

						<Separator />

						<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
							<div>
								<p className="text-sm font-medium">
									{user_profile_delete_account_title()}
								</p>
								<p className="text-xs text-muted-foreground">
									{user_profile_delete_account_subtitle()}
								</p>
							</div>

							<Button
								variant="destructive"
								size="sm"
								className="cursor-pointer"
								onClick={() => setDeleteAccountOpen(true)}
							>
								{user_profile_delete_account_title()}
							</Button>
						</div>
					</div>
				</CardContent>
			</Card>

		<ConfirmDeleteAccountModal
			open={deleteAccountOpen}
			onOpenChange={setDeleteAccountOpen}
			onConfirm={async () => {
				await deleteMe();
			}}
		/>

		<RegisterTenantModal
			open={registerOpen}
			onOpenChange={setRegisterOpen}
			language={language}
		/>

		<ApiKeyOneTimeModal
				open={apiKeyModalOpen}
				apiKey={generatedApiKey}
				clientId={me?.id ?? null}
				onOpenChange={(v) => {
					setApiKeyModalOpen(v);
					if (!v) setGeneratedApiKey(null);
				}}
			/>

			<ConfirmDeleteApiKeyModal
				open={deleteApiKeyOpen}
				onOpenChange={setDeleteApiKeyOpen}
				onConfirm={handleDeleteApiKey}
			/>
		</div>
	);
}

// ------------------------------------------------------------------------------------------
// ---------------------------------------- DELETE MODAL ------------------------------------
// ------------------------------------------------------------------------------------------
function ConfirmDeleteAccountModal({
	open,
	onOpenChange,
	onConfirm,
}: {
	open: boolean;
	onOpenChange: (v: boolean) => void;
	onConfirm: () => void;
}) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<span className="flex h-8 w-8 items-center justify-center rounded-full">
							<AlertTriangle className="h-4 w-4 text-destructive" />
						</span>
						{user_profile_delete_account_title()}
					</DialogTitle>
					<DialogDescription className="pt-1">
						{user_profile_delete_account_description()}
					</DialogDescription>
				</DialogHeader>

				<DialogFooter className="flex justify-end gap-2">
					<Button
						variant="outline"
						className="cursor-pointer"
						onClick={() => onOpenChange(false)}
					>
						{common_cancel()}
					</Button>

					<Button
						variant="destructive"
						className="cursor-pointer"
						onClick={onConfirm}
					>
						{common_confirm()}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ------------------------------------------------------------------------------------------
// --------------------------------- ONE TIME APIKEY MODAL ----------------------------------
// ------------------------------------------------------------------------------------------
function ApiKeyOneTimeModal({
	open,
	onOpenChange,
	apiKey,
	clientId,
}: {
	open: boolean;
	onOpenChange: (v: boolean) => void;
	apiKey: string | null;
	clientId: string | null;
}) {
	const [copiedKey, setCopiedKey] = useState(false);
	const [copiedClientId, setCopiedClientId] = useState(false);

	useEffect(() => {
		if (open) {
			setCopiedKey(false);
			setCopiedClientId(false);
		}
	}, [open]);

	if (!apiKey) return null;

	const handleCopyKey = async () => {
		await navigator.clipboard.writeText(apiKey);
		setCopiedKey(true);
	};

	const handleCopyClientId = async () => {
		if (!clientId) return;
		await navigator.clipboard.writeText(clientId);
		setCopiedClientId(true);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
							<KeyRound className="h-4 w-4 text-primary" />
						</span>
						{user_profile_api_keys_one_time_modal_title()}
					</DialogTitle>

					<DialogDescription className="pt-1">
						{user_profile_api_keys_one_time_modal_description()}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-3">
					{clientId && (
						<div className="space-y-1">
							<label className="text-xs font-medium text-muted-foreground">
								{user_profile_api_keys_one_time_modal_client_id_label()}
							</label>
							<div className="flex items-center gap-2">
								<Input readOnly value={clientId} className="font-mono" />

								<Button
									size="icon"
									variant="outline"
									onClick={handleCopyClientId}
									className="cursor-pointer"
									aria-label={user_profile_api_keys_one_time_modal_copy_client_id_aria_label()}
								>
									{copiedClientId ? (
										<Check className="h-4 w-4 text-green-600" />
									) : (
										<Copy className="h-4 w-4" />
									)}
								</Button>
							</div>
						</div>
					)}

					<div className="space-y-1">
						<label className="text-xs font-medium text-muted-foreground">
							{user_profile_api_keys_one_time_modal_copy_aria_label()}
						</label>
						<div className="flex items-center gap-2">
							<Input readOnly value={apiKey} className="font-mono" />

							<Button
								size="icon"
								variant="outline"
								onClick={handleCopyKey}
								className="cursor-pointer"
								aria-label={user_profile_api_keys_one_time_modal_copy_aria_label()}
							>
								{copiedKey ? (
									<Check className="h-4 w-4 text-green-600" />
								) : (
									<Copy className="h-4 w-4" />
								)}
							</Button>
						</div>
					</div>

					<p className="text-xs text-muted-foreground">
						{user_profile_api_keys_one_time_modal_security_note()}
					</p>
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						className="cursor-pointer"
						onClick={() => onOpenChange(false)}
					>
						{user_profile_api_keys_one_time_modal_saved_button()}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ------------------------------------------------------------------------------------------
// ----------------------------------- DELETE APIKEY MODAL ----------------------------------
// ------------------------------------------------------------------------------------------
function ConfirmDeleteApiKeyModal({
	open,
	onOpenChange,
	onConfirm,
}: {
	open: boolean;
	onOpenChange: (v: boolean) => void;
	onConfirm: () => void;
}) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						{user_profile_api_keys_delete_modal_title()}
					</DialogTitle>
					<DialogDescription className="pt-1">
						{user_profile_api_keys_delete_modal_description()}
					</DialogDescription>
				</DialogHeader>

				<DialogFooter className="flex justify-end gap-2 cursor-pointer">
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						<span>{common_cancel()}</span>
					</Button>
					<Button
						className="cursor-pointer"
						variant="destructive"
						onClick={onConfirm}
					>
						<span>{common_confirm()}</span>
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ------------------------------------------------------------------------------------------
// ------------------------------------- SKELETON LOADING -----------------------------------
// ------------------------------------------------------------------------------------------

function ProfileSkeleton() {
	return (
		<div className="flex flex-col gap-6 px-3 py-4 w-full max-w-screen-2xl mx-auto animate-pulse">
			{/* HEADER */}
			<header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="space-y-2">
					<div className="h-8 w-48 bg-muted rounded-md" />
					<div className="h-4 w-72 bg-muted rounded-md opacity-60" />
				</div>

				<div className="flex items-center gap-2">
					<div className="h-9 w-36 bg-muted rounded-md" />
					<div className="h-9 w-28 bg-muted rounded-md" />
				</div>
			</header>

			<Separator />

			{/* PROFILE CARD */}
			<Card className="bg-sidebar">
				<CardContent className="p-6 space-y-6">
					<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
						<div className="flex items-center gap-5">
							<div className="h-16 w-16 rounded-full bg-muted shrink-0" />

							<div className="space-y-2">
								<div className="flex items-center gap-2">
									<div className="h-5 w-40 bg-muted rounded-md" />
									<div className="h-5 w-14 bg-muted rounded-md" />
									<div className="h-8 w-8 bg-muted rounded-md" />
								</div>

								<div className="h-4 w-56 bg-muted rounded-md opacity-60" />
							</div>
						</div>

						<div className="flex items-center gap-4 rounded-lg border border-border bg-muted/40 px-4 py-2">
							<div className="h-6 w-16 bg-muted rounded-md" />
							<Separator orientation="vertical" className="h-6" />
							<div className="h-4 w-24 bg-muted rounded-md opacity-60" />
							<Separator orientation="vertical" className="h-6" />
							<div className="h-6 w-10 bg-muted rounded-md" />
						</div>
					</div>

					<Separator />

					{/* DEVICES SECTION */}
					<div className="space-y-4">
						<div className="space-y-2">
							<div className="h-6 w-40 bg-muted rounded-md" />
							<div className="h-4 w-64 bg-muted rounded-md opacity-60" />
						</div>

						<div className="space-y-3">
							{[1, 2, 3].map((i) => (
								<div
									key={i}
									className="flex items-center justify-between rounded-lg border border-border px-4 py-3"
								>
									<div className="space-y-2 w-full max-w-md">
										<div className="h-4 w-72 bg-muted rounded-md" />
										<div className="h-3 w-56 bg-muted rounded-md opacity-60" />
										<div className="h-3 w-40 bg-muted rounded-md opacity-40" />
									</div>

									<div className="h-9 w-9 bg-muted rounded-md shrink-0" />
								</div>
							))}
						</div>
					</div>
				</CardContent>
			</Card>

			{/* API KEY + DANGER ZONE CARD */}
			<Card className="bg-sidebar">
				<CardContent className="p-6 space-y-6">
					{/* API KEY SECTION */}
					<div className="space-y-3">
						<div className="space-y-2">
							<div className="h-6 w-48 bg-muted rounded-md" />
							<div className="h-4 w-72 bg-muted rounded-md opacity-60" />
						</div>

						<div className="flex items-center gap-3">
							<div className="flex-1">
								<div className="h-10 w-full bg-muted rounded-md" />
							</div>

							<div className="flex gap-2">
								<div className="h-9 w-9 bg-muted rounded-md" />
								<div className="h-9 w-9 bg-muted rounded-md" />
							</div>
						</div>
					</div>

					<Separator />

					{/* DANGER ZONE */}
					<div className="rounded-lg border border-destructive/30 bg-destructive/5 p-5 space-y-4">
						<div className="space-y-2">
							<div className="h-4 w-32 bg-muted rounded-md" />
							<div className="h-3 w-64 bg-muted rounded-md opacity-60" />
						</div>

						<Separator />

						<div className="flex items-center justify-between">
							<div className="space-y-2">
								<div className="h-4 w-40 bg-muted rounded-md" />
								<div className="h-3 w-56 bg-muted rounded-md opacity-60" />
							</div>

							<div className="h-9 w-36 bg-muted rounded-md" />
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
